/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardReOpenReasonProps {
  id: string;
  name: string;
  affectsTrip: boolean;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CardReOpenReasonRecord extends Record<CardReOpenReasonProps>({
  id: null as any,
  name: null as any,
  affectsTrip: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    affectsTrip: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CardReOpenReasonRecord {
    if (json == null) {
      throw new Error('CardReOpenReasonRecord.fromJS: parameter cannot be null');
    }
    return new CardReOpenReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardReOpenReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'CardReOpenReasonJson.name'),
      affectsTrip: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'affectsTrip', false),
        'CardReOpenReasonJson.affectsTrip',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    affectsTrip: boolean;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CardReOpenReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('CardReOpenReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardReOpenReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardReOpenReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'CardReOpenReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      affectsTrip: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'affectsTrip', false),
            'CardReOpenReasonJson.affectsTrip',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CardReOpenReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('CardReOpenReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardReOpenReasonViewModelBase implements CardReOpenReasonProps {
  protected readonly _props: CardReOpenReasonProps;
  private _json?: CardReOpenReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get affectsTrip(): boolean {
    return this._props.affectsTrip;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: CardReOpenReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        affectsTrip: this._props.affectsTrip,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardReOpenReasonEntities = (state: AppState) =>
  state.entityStores.cardReOpenReason.entities;

export const selectCardReOpenReasonById = (id: string) => (state: AppState) =>
  state.entityStores.cardReOpenReason.entities.get(id);

export const selectCardReOpenReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cardReOpenReason.meta.get(id);

export const selectCardReOpenReasonAndMetadataById = (id: string) =>
  createSelector(
    selectCardReOpenReasonById(id),
    selectCardReOpenReasonMetadataById(id),
    (cardReOpenReason, meta) => ({ cardReOpenReason, meta }),
  );

export function selectCardReOpenReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardReOpenReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardReOpenReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardReOpenReasonEntitiesByName(name: string) {
  return createSelector(selectAllCardReOpenReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectCardReOpenReasonEntitiesByAffectsTrip(affectsTrip: boolean) {
  return createSelector(selectAllCardReOpenReasonEntities, (entities) =>
    entities.filter((e) => e.affectsTrip === affectsTrip).valueSeq(),
  );
}
//#endregion Selectors
